'use client'

import { useEffect, useState } from 'react'
import {
	acceptAll,
	acceptEssentialOnly,
	type CookieConsent,
	getConsent,
	setConsent,
} from '@/lib/cookie-consent'

const COOKIE_POLICY_URL = process.env.NEXT_PUBLIC_CATALOG_URL
	? `${process.env.NEXT_PUBLIC_CATALOG_URL}/cookies`
	: '/cookies'

export function CookieConsentBanner() {
	const [visible, setVisible] = useState(false)
	const [showPreferences, setShowPreferences] = useState(false)
	const [analytics, setAnalytics] = useState(false)
	const [functional, setFunctional] = useState(false)

	useEffect(() => {
		const existing = getConsent()
		if (!existing) {
			setVisible(true)
		}
	}, [])

	useEffect(() => {
		function handleReopen() {
			const existing = getConsent()
			setAnalytics(existing?.analytics ?? false)
			setFunctional(existing?.functional ?? false)
			setShowPreferences(true)
			setVisible(true)
		}
		window.addEventListener('cookie-consent-reopen', handleReopen)
		return () => window.removeEventListener('cookie-consent-reopen', handleReopen)
	}, [])

	function handleAcceptAll() {
		const consent = acceptAll()
		setVisible(false)
		window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: consent }))
	}

	function handleRejectNonEssential() {
		const consent = acceptEssentialOnly()
		setVisible(false)
		window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: consent }))
	}

	function handleSavePreferences() {
		const consent: CookieConsent = { essential: true, analytics, functional }
		setConsent(consent)
		setVisible(false)
		setShowPreferences(false)
		window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: consent }))
	}

	if (!visible) return null

	return (
		<div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
			<div className="mx-auto max-w-2xl rounded-lg border border-border bg-background p-4 shadow-lg md:p-6">
				{!showPreferences ? (
					<>
						<p className="mb-4 text-sm text-muted-foreground">
							We use cookies to keep you signed in and to improve our services. Some cookies are essential,
							while others help us understand how you use Extenshi.{' '}
							<a href={COOKIE_POLICY_URL} className="underline hover:text-foreground">
								Learn more
							</a>
						</p>
						<div className="flex flex-wrap items-center gap-2">
							<button
								type="button"
								onClick={handleAcceptAll}
								className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
							>
								Accept All
							</button>
							<button
								type="button"
								onClick={handleRejectNonEssential}
								className="inline-flex h-9 items-center rounded-md border border-input bg-background px-4 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
							>
								Reject All
							</button>
							<button
								type="button"
								onClick={() => setShowPreferences(true)}
								className="inline-flex h-9 items-center px-4 text-sm font-medium text-muted-foreground hover:text-foreground"
							>
								Manage Preferences
							</button>
						</div>
					</>
				) : (
					<>
						<h3 className="mb-3 text-sm font-semibold text-foreground">Cookie Preferences</h3>
						<div className="mb-4 space-y-3">
							<label className="flex items-start gap-3">
								<input type="checkbox" checked disabled className="mt-0.5 accent-primary" />
								<span className="text-sm">
									<span className="font-medium text-foreground">Essential</span>
									<span className="block text-muted-foreground">
										Authentication and security. Always active.
									</span>
								</span>
							</label>
							<label className="flex items-start gap-3">
								<input
									type="checkbox"
									checked={analytics}
									onChange={(e) => setAnalytics(e.target.checked)}
									className="mt-0.5 accent-primary"
								/>
								<span className="text-sm">
									<span className="font-medium text-foreground">Analytics</span>
									<span className="block text-muted-foreground">
										Help us understand usage and improve the service.
									</span>
								</span>
							</label>
							<label className="flex items-start gap-3">
								<input
									type="checkbox"
									checked={functional}
									onChange={(e) => setFunctional(e.target.checked)}
									className="mt-0.5 accent-primary"
								/>
								<span className="text-sm">
									<span className="font-medium text-foreground">Functional</span>
									<span className="block text-muted-foreground">
										Live chat support and theme preferences across subdomains.
									</span>
								</span>
							</label>
						</div>
						<div className="flex flex-wrap items-center gap-2">
							<button
								type="button"
								onClick={handleSavePreferences}
								className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
							>
								Save Preferences
							</button>
							<button
								type="button"
								onClick={() => setShowPreferences(false)}
								className="inline-flex h-9 items-center px-4 text-sm font-medium text-muted-foreground hover:text-foreground"
							>
								Back
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	)
}
